import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { RotateCw, AlertCircle } from 'lucide-react';
import { DEFAULT_INPUTS, FIELD_SPECS, SPEED_UNITS, validateField, validateSpeedValueForUnit } from '../../lib/vehicleSpeedRpmTools';
import { vehicleSpeedRpmToolApi } from '../../lib/toolsApi';
import { useQueryPrefill } from '../../lib/queryPrefill';

const CALC_DEBOUNCE_MS = 350;

const SPEC_BY_KEY = Object.fromEntries(FIELD_SPECS.map((s) => [s.key, s]));

const DRIVETRAIN_KEYS = ['wheelRadiusM', 'gearRatio', 'finalDriveRatio'];

// Query-param prefill contract: ?wheelRadiusM=0.3172 (sent by the Tyre Size Calculator).
const PREFILL_SPECS = FIELD_SPECS.filter((s) => s.key === 'wheelRadiusM');

function toRawStrings(defaults) {
  return Object.fromEntries(Object.entries(defaults).map(([k, v]) => [k, String(v)]));
}

const { speedUnit: DEFAULT_SPEED_UNIT, ...NUMERIC_DEFAULTS } = DEFAULT_INPUTS;

const VehicleSpeedRpmCalculator = () => {
  const [speedUnit, setSpeedUnit] = useState(DEFAULT_SPEED_UNIT);
  const prefill = useQueryPrefill(PREFILL_SPECS, validateField);
  const [raw, setRaw] = useState(() => ({ ...toRawStrings(NUMERIC_DEFAULTS), ...prefill.values }));

  const setField = (key, value) => setRaw((prev) => ({ ...prev, [key]: value }));

  const unitInfo = SPEED_UNITS.find((u) => u.value === speedUnit) || SPEED_UNITS[0];

  const { fieldErrors, values } = useMemo(() => {
    const errors = {};
    const parsed = {};
    for (const spec of FIELD_SPECS) {
      const err = validateField(spec, raw[spec.key]);
      if (err) errors[spec.key] = err;
      else parsed[spec.key] = Number(raw[spec.key]);
    }
    if (!errors.speedValue) {
      const unitErr = validateSpeedValueForUnit(parsed.speedValue, speedUnit);
      if (unitErr) errors.speedValue = unitErr;
    }
    return { fieldErrors: errors, values: parsed };
  }, [raw, speedUnit]);

  const hasErrors = Object.keys(fieldErrors).length > 0;

  const [results, setResults] = useState(null);
  const [calcError, setCalcError] = useState(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (hasErrors) {
      setResults(null);
      setCalcError(null);
      return;
    }
    const requestId = ++requestIdRef.current;
    const timer = setTimeout(() => {
      vehicleSpeedRpmToolApi.convert({ ...values, speedUnit })
        .then((outcome) => {
          if (requestId !== requestIdRef.current) return;
          if (outcome.errors) {
            setResults(null);
            setCalcError(Object.values(outcome.errors)[0]);
          } else {
            setResults(outcome.results);
            setCalcError(null);
          }
        })
        .catch((e) => {
          if (requestId !== requestIdRef.current) return;
          setResults(null);
          setCalcError(e.message);
        });
    }, CALC_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [values, speedUnit, hasErrors]);

  const renderField = (key) => {
    const spec = SPEC_BY_KEY[key];
    const error = fieldErrors[key];
    return (
      <div className="space-y-2" key={key}>
        <Label htmlFor={`vehicle-speed-rpm-${key}`} className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
          {spec.label} <span className="normal-case text-muted-foreground/70">({spec.unit})</span>
        </Label>
        <Input
          id={`vehicle-speed-rpm-${key}`}
          type="number"
          step="any"
          value={raw[key]}
          onChange={(e) => setField(key, e.target.value)}
          className="font-mono"
          aria-invalid={!!error}
          aria-describedby={error ? `vehicle-speed-rpm-${key}-error` : undefined}
          data-testid={`vehicle-speed-rpm-${key}-input`}
        />
        {error && (
          <p id={`vehicle-speed-rpm-${key}-error`} className="flex items-center gap-1 text-xs text-destructive" data-testid={`vehicle-speed-rpm-${key}-error`}>
            <AlertCircle className="h-3 w-3 shrink-0" /> {error}
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <Card className="technical-card lg:col-span-3">
        <CardHeader>
          <CardTitle className="font-chivo uppercase tracking-tight flex items-center gap-2">
            <RotateCw className="h-5 w-5 text-autoxium-blue" />
            Vehicle Speed to RPM/RPS
          </CardTitle>
          <CardDescription>
            Convert vehicle speed (km/h or m/s) to wheel and motor/engine shaft rotational
            speed, in both RPM and RPS. Results update live as you edit.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4 p-4 bg-muted/50 rounded-md">
            <h3 className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Vehicle Speed
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="vehicle-speed-rpm-speedValue" className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                  Vehicle Speed <span className="normal-case text-muted-foreground/70">({unitInfo.label})</span>
                </Label>
                <Input
                  id="vehicle-speed-rpm-speedValue"
                  type="number"
                  step="any"
                  value={raw.speedValue}
                  onChange={(e) => setField('speedValue', e.target.value)}
                  className="font-mono"
                  aria-invalid={!!fieldErrors.speedValue}
                  aria-describedby={fieldErrors.speedValue ? 'vehicle-speed-rpm-speedValue-error' : undefined}
                  data-testid="vehicle-speed-rpm-speedValue-input"
                />
                {fieldErrors.speedValue && (
                  <p id="vehicle-speed-rpm-speedValue-error" className="flex items-center gap-1 text-xs text-destructive" data-testid="vehicle-speed-rpm-speedValue-error">
                    <AlertCircle className="h-3 w-3 shrink-0" /> {fieldErrors.speedValue}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                  Speed Input Mode
                </Label>
                <Select value={speedUnit} onValueChange={setSpeedUnit}>
                  <SelectTrigger data-testid="vehicle-speed-rpm-unit-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SPEED_UNITS.map((u) => (
                      <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="space-y-4 p-4 bg-muted/50 rounded-md">
            <h3 className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Wheel &amp; Drivetrain
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {DRIVETRAIN_KEYS.map(renderField)}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="lg:col-span-2 space-y-4">
        <Card className="technical-card h-fit">
          <CardHeader>
            <CardTitle className="font-chivo uppercase tracking-tight text-base">Rotational Speed</CardTitle>
          </CardHeader>
          <CardContent>
            {hasErrors ? (
              <p className="text-sm text-muted-foreground" data-testid="vehicle-speed-rpm-results-placeholder">
                Fix the highlighted fields to see results.
              </p>
            ) : calcError ? (
              <div className="flex items-center gap-2 text-sm text-destructive" data-testid="vehicle-speed-rpm-calc-error">
                <AlertCircle className="h-4 w-4 shrink-0" /> {calcError}
              </div>
            ) : !results ? (
              <p className="text-sm text-muted-foreground" data-testid="vehicle-speed-rpm-results-loading">
                Calculating…
              </p>
            ) : (
              <div className="space-y-4" data-testid="vehicle-speed-rpm-results">
                <div>
                  <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1">Wheel Rotational Speed</p>
                  <p className="font-mono text-lg font-bold text-autoxium-blue">
                    {results.wheelRpm.toFixed(1)} RPM
                  </p>
                  <p className="font-mono text-sm text-muted-foreground">
                    {results.wheelRps.toFixed(3)} RPS
                  </p>
                </div>
                <div className="pt-4 border-t border-structure">
                  <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1">Motor/Engine Shaft Rotational Speed</p>
                  <p className="font-mono text-lg font-bold text-autoxium-blue">
                    {results.motorRpm.toFixed(1)} RPM
                  </p>
                  <p className="font-mono text-sm text-muted-foreground">
                    {results.motorRps.toFixed(3)} RPS
                  </p>
                </div>
                <div className="pt-4 border-t border-structure">
                  <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1">
                    Vehicle Speed <span className="normal-case text-muted-foreground/70">(converted)</span>
                  </p>
                  <p className="font-mono text-sm font-bold">{results.speedMs.toFixed(2)} m/s</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="technical-card" data-testid="vehicle-speed-rpm-sources">
          <CardHeader>
            <CardTitle className="font-chivo uppercase tracking-tight text-sm">Sources &amp; Assumptions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-xs text-muted-foreground leading-relaxed">
            <div>
              <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">method</Badge>
              Wheel angular speed ω = v ÷ r_wheel (rad/s), converted to RPS via ω ÷ (2·π) and
              to RPM via RPS × 60 (π = 3.14159). Motor/engine shaft speed multiplies wheel RPM
              by both the gear ratio and the final drive ratio.
            </div>
            <div>
              <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">behavior</Badge>
              Switching the Speed Input Mode does not convert the number already entered —
              it only changes how that number is interpreted (km/h vs m/s). Re-enter the
              speed value after switching modes.
            </div>
            <div>
              <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle border-amber-500/50 text-amber-700">verify</Badge>
              Use the tire's actual rolling (dynamic) radius under load, not its unloaded
              static radius, for an accurate wheel RPM.
            </div>
            <div>
              <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">defaults</Badge>
              60 km/h, a 0.30 m wheel radius, a 4.0 gear ratio, and a 3.5 final drive ratio
              are typical passenger-vehicle figures, not mandated standards — all inputs are
              editable for your own vehicle and scenario.
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VehicleSpeedRpmCalculator;
